import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  X,
  Search,
  CheckCircle
} from 'lucide-react';

const Packages = () => {
  const [packages, setPackages] = useState([
    {
      id: 1,
      name: 'Starter',
      description: 'Perfect for small salons just getting started',
      price: 49,
      billingCycle: 'monthly',
      userLimit: 2,
      features: [
        'Basic salon management',
        '2 user accounts',
        'Email support',
        'Basic reporting',
        'Mobile app access'
      ],
      status: 'active',
      companies: 45,
      revenue: '$2,205'
    },
    {
      id: 2,
      name: 'Professional',
      description: 'Ideal for growing salons with multiple staff',
      price: 99,
      billingCycle: 'monthly',
      userLimit: 5,
      features: [
        'Advanced salon management',
        '5 user accounts',
        'Priority support',
        'Advanced reporting',
        'Inventory management',
        'Customer analytics',
        'Appointment scheduling'
      ],
      status: 'active',
      companies: 28,
      revenue: '$2,772'
    },
    {
      id: 3,
      name: 'Enterprise',
      description: 'Complete solution for large salon chains',
      price: 199,
      billingCycle: 'monthly',
      userLimit: 10,
      features: [
        'Full salon management suite',
        '10 user accounts',
        '24/7 phone support',
        'Custom reporting',
        'Advanced inventory',
        'Multi-location support',
        'API access',
        'White-label options',
        'Dedicated account manager'
      ],
      status: 'active',
      companies: 12,
      revenue: '$2,388'
    },
    {
      id: 4,
      name: 'Basic',
      description: 'Simple package for individual stylists',
      price: 29,
      billingCycle: 'monthly',
      userLimit: 1,
      features: [
        'Basic appointment booking',
        '1 user account',
        'Email support',
        'Simple calendar'
      ],
      status: 'archived',
      companies: 8,
      revenue: '$232'
    }
  ]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    price: '',
    billingCycle: 'monthly',
    userLimit: '',
    features: [''],
    status: 'active'
  });

  const handleCreatePackage = () => {
    setShowCreateModal(true);
    setNewPackage({
      name: '',
      description: '',
      price: '',
      billingCycle: 'monthly',
      userLimit: '',
      features: [''],
      status: 'active'
    });
  };

  const handleEditPackage = (pkg) => {
    setSelectedPackage(pkg);
    setNewPackage({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price,
      billingCycle: pkg.billingCycle,
      userLimit: pkg.userLimit,
      features: [...pkg.features],
      status: pkg.status
    });
    setShowEditModal(true);
  };

  const handleDeletePackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowDeleteModal(true);
  };

  const handleSavePackage = () => {
    if (showEditModal && selectedPackage) {
      // Edit existing package
      setPackages(packages.map(pkg => 
        pkg.id === selectedPackage.id 
          ? { ...pkg, ...newPackage }
          : pkg
      ));
    } else {
      // Create new package
      const newPkg = {
        ...newPackage,
        id: Date.now(),
        price: parseFloat(newPackage.price),
        userLimit: parseInt(newPackage.userLimit),
        companies: 0,
        revenue: '$0'
      };
      setPackages([...packages, newPkg]);
    }
    
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedPackage(null);
  };

  const handleConfirmDelete = () => {
    setPackages(packages.filter(pkg => pkg.id !== selectedPackage.id));
    setShowDeleteModal(false);
    setSelectedPackage(null);
  };

  const addFeature = () => {
    setNewPackage({ ...newPackage, features: [...newPackage.features, ''] });
  };

  const removeFeature = (index) => {
    const updatedFeatures = newPackage.features.filter((_, i) => i !== index);
    setNewPackage({ ...newPackage, features: updatedFeatures });
  };

  const updateFeature = (index, value) => {
    const updatedFeatures = [...newPackage.features];
    updatedFeatures[index] = value;
    setNewPackage({ ...newPackage, features: updatedFeatures });
  };

  const filteredPackages = packages.filter(pkg => {
    const matchesStatus = filterStatus === 'all' || pkg.status === filterStatus;
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getBillingCycleColor = (cycle) => {
    switch (cycle) {
      case 'monthly': return 'bg-blue-100 text-blue-800';
      case 'yearly': return 'bg-purple-100 text-purple-800';
      case 'quarterly': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Package Management</h1>
          <p className="text-gray-600">Create and manage subscription packages for salon companies</p>
        </div>
        <button 
          onClick={handleCreatePackage}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Package</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search packages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <div className="text-sm text-gray-500">
          {filteredPackages.length} of {packages.length} packages
        </div>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPackages.map((pkg) => (
          <div key={pkg.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="p-6">
              {/* Package Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pkg.status)}`}>
                    {pkg.status}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBillingCycleColor(pkg.billingCycle)}`}>
                    {pkg.billingCycle}
                  </span>
                </div>
              </div>

              {/* Pricing */}
              <div className="mb-4">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900">${pkg.price}</span>
                  <span className="text-gray-500 ml-1">/{pkg.billingCycle}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{pkg.userLimit} user{pkg.userLimit > 1 ? 's' : ''} included</p>
              </div>

              {/* Features */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Features:</h4>
                <ul className="space-y-1">
                  {pkg.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                  {pkg.features.length > 3 && (
                    <li className="text-sm text-gray-500">
                      +{pkg.features.length - 3} more features
                    </li>
                  )}
                </ul>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-900">{pkg.companies}</p>
                  <p className="text-xs text-gray-500">Companies</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-900">{pkg.revenue}</p>
                  <p className="text-xs text-gray-500">Revenue</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button 
                  onClick={() => handleEditPackage(pkg)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </button>
                <button 
                  onClick={() => handleDeletePackage(pkg)}
                  className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Package Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-800">
                {showEditModal ? 'Edit Package' : 'Create New Package'}
              </h3>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedPackage(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSavePackage(); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Package Name</label>
                  <input
                    type="text"
                    value={newPackage.name}
                    onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g., Professional"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                    <input
                      type="number"
                      value={newPackage.price}
                      onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="99"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newPackage.description}
                  onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Describe what this package offers..."
                  rows="3"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Billing Cycle</label>
                  <select
                    value={newPackage.billingCycle}
                    onChange={(e) => setNewPackage({ ...newPackage, billingCycle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User Limit</label>
                  <input
                    type="number"
                    value={newPackage.userLimit}
                    onChange={(e) => setNewPackage({ ...newPackage, userLimit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="5"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={newPackage.status}
                    onChange={(e) => setNewPackage({ ...newPackage, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

  <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                <div className="space-y-2">
                  {newPackage.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Enter feature description"
                        required
                      />
                      {newPackage.features.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="p-2 text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Feature
                  </button>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedPackage(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {showEditModal ? 'Update Package' : 'Create Package'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Package</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{selectedPackage?.name}"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Package
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
  </div>
);
};

export default Packages;
